import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { fileToCompressedDataUrl } from "@/lib/image";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { PlusIcon, XIcon, GearIcon, CoinsIcon, TrashIcon, CheckCircleIcon, TableIcon, ClockIcon, FloppyDiskIcon, ChartBarIcon, FileXlsIcon, FilePdfIcon } from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

const SOPORTE_TIPOS = [
  { v: "visita", l: "Visita al cliente" },
  { v: "gestion_cobro", l: "Gestión de cobro" },
  { v: "documentacion", l: "Documentación" },
];
const pendienteLiq = (a) => (a.estado === "acumulada" ? Math.max(0, (a.monto_liberado || 0) - (a.monto_pagado || 0)) : 0);

export default function Commissions() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "accountant";

  const [tab, setTab] = useState("acumuladas");
  const [accruals, setAccruals] = useState([]);
  const [summary, setSummary] = useState([]);
  const [reps, setReps] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [supportModal, setSupportModal] = useState(null);
  const [reviewModal, setReviewModal] = useState(null);
  const [supportForm, setSupportForm] = useState({ file: null, descripcion: "", tipo: "visita" });
  const [filterRtc, setFilterRtc] = useState("");
  const [liqModal, setLiqModal] = useState(null);
  const [liqCuenta, setLiqCuenta] = useState("");
  // COM-01 — categorías de producto activas para vincular reglas
  const [productCategories, setProductCategories] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [a, s, rep, ac, cats] = await Promise.all([
        api.get("/commissions/accruals", { params: filterRtc ? { rtc_id: filterRtc } : {} }),
        api.get("/commissions/summary"),
        api.get("/representatives"),
        api.get("/treasury/accounts").catch(() => ({ data: [] })),
        api.get("/products/categories").catch(() => ({ data: [] })),
      ]);
      setAccruals(a.data); setSummary(s.data); setReps(rep.data); setAccounts(ac.data);
      setProductCategories(cats.data || []);
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filterRtc]);

  const toggleSel = (id) => { const s = new Set(selected); if (s.has(id)) s.delete(id); else s.add(id); setSelected(s); };
  const cerrarSoporte = () => { setSupportModal(null); setSupportForm({ file: null, descripcion: "", tipo: "visita" }); };
  const subirSoporte = async () => {
    try {
      if (!supportForm.file) { toast.error("Selecciona una foto/PDF"); return; }
      const url = await fileToCompressedDataUrl(supportForm.file);
      await api.post(`/commissions/accruals/${supportModal.id}/soporte`, { url, descripcion: supportForm.descripcion, tipo: supportForm.tipo });
      toast.success("Soporte subido. Esperando aprobación admin.");
      cerrarSoporte(); load();
    } catch (er) { toast.error(apiErr(er) || er.message); }
  };
  const liquidate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/commissions/liquidate", { rtc_id: liqModal.rtc_id, accrual_ids: Array.from(selected), cuenta_id: liqCuenta || null });
      toast.success("GRATIVA liquidada");
      setLiqModal(null); setSelected(new Set()); setLiqCuenta("");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const selTotal = accruals.filter((a) => selected.has(a.id)).reduce((s, a) => s + pendienteLiq(a), 0);

  const lm = useModalForm({ value: liqCuenta, abierto: Boolean(liqModal), onClose: () => setLiqModal(null), onSave: liquidate });
  const sm = useModalForm({ value: { d: supportForm.descripcion, t: supportForm.tipo, f: supportForm.file?.name }, abierto: Boolean(supportModal), onClose: cerrarSoporte, onSave: subirSoporte });
  const rv = useModalForm({ abierto: Boolean(reviewModal), onClose: () => setReviewModal(null) });
  return (
    <Layout title="GRATIVA · Gratificación Variable" subtitle="Cálculo GRATIVA automático sobre cobros de ventas por RTC (Tabla GRATIVA × puntualidad)">
      <div className="flex gap-1 mb-4 border-b border-border/60">
        {[
          {k: "acumuladas", l: "Acumuladas", icon: CoinsIcon},
          {k: "resumen", l: "Resumen por RTC", icon: CheckCircleIcon},
          {k: "bonificacion", l: "Tabla GRATIVA", icon: TableIcon},
          {k: "puntualidad", l: "Tabla puntualidad", icon: ClockIcon},
          {k: "reporte", l: "Reporte GRATIVA", icon: ChartBarIcon},
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`} className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 ${tab === t.k ? "border-agro-green text-agro-green" : "border-transparent text-text-secondary hover:text-text-primary"}`}>
            <t.icon size={16} /> {t.l}
          </button>
        ))}
      </div>

      {tab === "acumuladas" && (
        <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
          <div className="p-3 border-b border-border/60 flex gap-3 items-center">
            <select value={filterRtc} onChange={(e) => setFilterRtc(e.target.value)} className="bg-app border border-border rounded-md px-3 py-1.5 text-sm">
              <option value="">Todos los RTCs</option>
              {reps.map((r) => <option key={r.id} value={r.id}>{r.codigo} · {r.nombre}</option>)}
            </select>
            <span className="text-[11px] text-text-muted">{accruals.length} líneas. Cada línea de factura genera 1 acumulación GRATIVA.</span>
          </div>
          <table className="w-full">
            <thead className="bg-sidebar">
              <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                <th className="p-3 pl-5 w-8"></th>
                <th className="p-3">RTC / Factura</th><th className="p-3">Producto</th>
                <th className="p-3">Cat. / Nivel</th>
                <th className="p-3 text-right">Pago línea</th>
                <th className="p-3 text-right">% × factor</th>
                <th className="p-3 text-right">GRATIVA bruta</th>
                <th className="p-3 text-right">Liberada / Retenida</th>
                <th className="p-3">Seguimiento</th>
                <th className="p-3 text-right pr-5">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="10" className="p-8 text-center text-text-muted">Cargando…</td></tr>
                : accruals.length === 0 ? <tr><td colSpan="10" className="p-8 text-center text-text-muted">Sin GRATIVA acumulada.</td></tr>
                : accruals.map((a) => (
                  <tr key={a.id} className="border-t border-border/40 hover:bg-white/[0.02]" data-testid={`accrual-${a.id}`}>
                    <td className="p-3 pl-5">{pendienteLiq(a) > 0.005 && canManage && <input type="checkbox" checked={selected.has(a.id)} onChange={() => toggleSel(a.id)} className="accent-agro-green" />}</td>
                    <td className="p-3 text-xs"><div className="font-medium">{a.rtc_nombre}</div><div className="font-mono text-agro-green">{a.factura_numero}</div><div className="text-text-muted">{a.cliente_nombre}</div></td>
                    <td className="p-3 text-xs"><div>{a.linea_producto_codigo || "—"}</div><div className="text-text-muted truncate max-w-[160px]">{a.linea_producto_descripcion}</div></td>
                    <td className="p-3 text-xs"><span className="px-2 py-0.5 rounded bg-app border border-border/60">{a.linea_categoria} {a.linea_nivel}</span> {a.es_campana && <span className="ml-1 text-[10px] text-blue-300">campaña</span>} {a.modo === "margen" && <div className="text-[10px] text-agro-yellow mt-1">modo margen</div>}{a.linea_proveedor_nombre && <div className="text-[10px] text-text-muted mt-0.5 truncate max-w-[140px]">{a.linea_proveedor_nombre}</div>}</td>
                    <td className="p-3 text-right text-xs">{fmtL(a.monto_pagado_linea)}</td>
                    <td className="p-3 text-right text-xs">{a.base_percent}% × {(a.factor_puntualidad * 100).toFixed(0)}% <div className="text-[10px] text-text-muted">{a.dias_atraso}d atraso</div></td>
                    <td className="p-3 text-right text-sm">{fmtL(a.bonificacion_bruta)}</td>
                    <td className="p-3 text-right text-xs"><span className="text-agro-green">{fmtL(a.monto_liberado)}</span> {a.monto_retenido > 0 && <span className="text-agro-yellow"> / {fmtL(a.monto_retenido)}</span>}{a.monto_anulado > 0 && <div className="text-[10px] text-red-400">anulado {fmtL(a.monto_anulado)}</div>}{a.monto_pagado > 0 && <div className="text-[10px] text-text-muted">pagado {fmtL(a.monto_pagado)}</div>}</td>
                    <td className="p-3">
                      {a.estado_seguimiento === "no_aplica" && <span className="text-[10px] text-text-muted">N/A</span>}
                      {a.estado_seguimiento === "pendiente_soporte" && <button onClick={() => setSupportModal(a)} className="text-[10px] px-2 py-0.5 rounded bg-agro-yellow/20 text-agro-yellow hover:bg-agro-yellow/30" data-testid={`upload-support-${a.id}`}>Subir soporte</button>}
                      {a.estado_seguimiento === "soporte_subido" && <button onClick={() => setReviewModal(a)} className="text-[10px] px-2 py-0.5 rounded bg-blue-500/20 text-blue-400" data-testid={`review-support-${a.id}`}>Revisar soporte</button>}
                      {a.estado_seguimiento === "liberado" && <span className="text-[10px] px-2 py-0.5 rounded bg-agro-green/20 text-agro-green">Liberado</span>}
                      {a.estado_seguimiento === "rechazado" && <span className="text-[10px] px-2 py-0.5 rounded bg-red-500/20 text-red-400" title={a.rechazo_motivo}>Rechazado · retenido anulado</span>}
                    </td>
                    <td className="p-3 pr-5 text-right">
                      <span className={`text-[10px] px-2 py-0.5 rounded ${a.estado === "acumulada" ? "bg-agro-yellow/20 text-agro-yellow" : a.estado === "anulada" ? "bg-red-500/20 text-red-400" : "bg-agro-green/20 text-agro-green"}`}>{a.estado}</span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          {selected.size > 0 && (
            <div className="p-3 border-t border-border/60 flex justify-between items-center bg-app">
              <div className="text-sm">Seleccionadas: <span className="text-agro-green font-semibold">{selected.size}</span> líneas · Liberado a liquidar: <span className="text-agro-green font-semibold">{fmtL(selTotal)}</span> <span className="text-[11px] text-text-muted">(la parte retenida se paga al aprobar el soporte)</span></div>
              <button onClick={() => {
                const first = accruals.find((a) => selected.has(a.id));
                if (first) setLiqModal({rtc_id: first.rtc_id, rtc_nombre: first.rtc_nombre, total: selTotal});
              }} data-testid="liquidate-btn" className="px-4 py-2 text-sm bg-agro-green text-black font-medium rounded-md hover:bg-agro-green-hover">Liquidar seleccionadas</button>
            </div>
          )}
        </div>
      )}

      {tab === "resumen" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {summary.length === 0 ? <div className="col-span-3 bg-card border border-border/60 rounded-lg p-8 text-center text-text-muted">Aún no hay GRATIVA generada. La GRATIVA (Gratificación Variable) se acumula automáticamente al cobrar facturas de venta por RTC.</div>
            : summary.map((s) => (
              <div key={s.rtc_id || "_g_"} className="bg-card border border-border/60 rounded-lg p-4">
                <div className="text-[10px] uppercase tracking-wider text-text-muted">{s.rtc_id ? "RTC" : "Global"}</div>
                <div className="font-heading font-semibold text-text-primary">{s.rtc_nombre || "—"}</div>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <div><div className="text-[10px] uppercase text-text-muted">Acumuladas</div><div className="text-agro-yellow font-semibold">{fmtL(s.acumulada || 0)}</div></div>
                  <div><div className="text-[10px] uppercase text-text-muted">Liquidadas</div><div className="text-agro-green font-semibold">{fmtL(s.liquidada || 0)}</div></div>
                  <div><div className="text-[10px] uppercase text-text-muted">Retenidas</div><div className="text-orange-300 font-semibold">{fmtL(s.retenida || 0)}</div></div>
                  <div><div className="text-[10px] uppercase text-text-muted">Anuladas</div><div className="text-red-400 font-semibold">{fmtL(s.anulada || 0)}</div></div>
                </div>
                <div className="text-[10px] text-text-muted mt-2">{s.count} movimientos</div>
              </div>
            ))}
        </div>
      )}

      {tab === "reporte" && <CommissionReport reps={reps} />}

      {liqModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...lm.overlayProps}>
          <div className="bg-card border border-border rounded-lg w-full max-w-md" data-testid="liq-modal">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold">Liquidar GRATIVA</h3>
              <button aria-label="Cerrar" onClick={lm.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
            </div>
            <form onSubmit={lm.guardar} className="p-5 space-y-3">
              <div className="bg-app p-3 rounded text-sm">
                <div>RTC: <span className="text-agro-green">{liqModal.rtc_nombre}</span></div>
                <div>Acumulados seleccionados: <span className="text-text-primary">{selected.size}</span></div>
                <div>Total GRATIVA a pagar: <span className="text-agro-green font-semibold">{fmtL(liqModal.total)}</span></div>
              </div>
              <div><label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Cuenta egreso</label><select value={liqCuenta} onChange={(e) => setLiqCuenta(e.target.value)} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" data-testid="liq-cuenta">
                <option value="">— Caja por defecto —</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.codigo} · {a.nombre}</option>)}
              </select></div>
              <div className="flex justify-end gap-2 border-t border-border/60 pt-3">
                <button type="button" onClick={lm.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary">Cancelar</button>
                <button disabled={lm.saving} type="submit" data-testid="liq-confirm" className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md">Confirmar liquidación</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {tab === "bonificacion" && <BonificationTable canManage={canManage} productCategories={productCategories} />}
      {tab === "puntualidad" && <PunctualityTable canManage={canManage} />}

      {supportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...sm.overlayProps}>
          <div className="bg-card border border-border rounded-lg w-full max-w-md" data-testid="support-upload-modal">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold">Subir soporte de seguimiento</h3>
              <button aria-label="Cerrar" onClick={sm.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
            </div>
            <form onSubmit={sm.guardar} className="p-5 space-y-3">
              <div className="bg-app p-3 rounded text-xs space-y-1">
                <div>Factura: <span className="font-mono text-agro-green">{supportModal.factura_numero}</span></div>
                <div>Producto: {supportModal.linea_producto_codigo} · {supportModal.linea_categoria} {supportModal.linea_nivel}</div>
                <div>Monto retenido: <span className="text-agro-yellow font-semibold">{fmtL(supportModal.monto_retenido)}</span></div>
              </div>
              <div><label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Tipo de soporte *</label>
                <select value={supportForm.tipo} onChange={(e) => setSupportForm({ ...supportForm, tipo: e.target.value })} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" data-testid="support-tipo">
                  {SOPORTE_TIPOS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
              </div>
              <div><label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Foto / PDF evidencia *</label><input required type="file" accept="image/*,.pdf" onChange={(e) => setSupportForm({ ...supportForm, file: e.target.files?.[0] })} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" data-testid="support-file" /></div>
              <div><label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Descripción</label><textarea rows={2} value={supportForm.descripcion} onChange={(e) => setSupportForm({ ...supportForm, descripcion: e.target.value })} placeholder="Visita realizada el…, gestión de cobro…" className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm resize-none" data-testid="support-desc" /></div>
              <div className="flex justify-end gap-2 border-t border-border/60 pt-3">
                <button type="button" onClick={sm.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary">Cancelar</button>
                <button disabled={sm.saving} type="submit" className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md" data-testid="support-submit">Subir</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {reviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...rv.overlayProps}>
          <div className="bg-card border border-border rounded-lg w-full max-w-2xl" data-testid="review-modal">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold">Revisar soporte</h3>
              <button aria-label="Cerrar" onClick={rv.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="bg-app p-3 rounded text-xs space-y-1">
                <div>Factura: <span className="font-mono text-agro-green">{reviewModal.factura_numero}</span> · Producto: {reviewModal.linea_producto_codigo}</div>
                <div>RTC: {reviewModal.rtc_nombre} · Monto retenido a liberar: <span className="text-agro-green font-semibold">{fmtL(reviewModal.monto_retenido)}</span></div>
                <div>Tipo: <span className="text-text-secondary">{(SOPORTE_TIPOS.find((t) => t.v === reviewModal.soporte_tipo) || {}).l || "—"}</span> · Subido por {reviewModal.soporte_subido_por || "—"}</div>
                <div>Descripción: <span className="text-text-secondary">{reviewModal.soporte_descripcion || "(sin descripción)"}</span></div>
              </div>
              {reviewModal.soporte_url && reviewModal.soporte_url.startsWith("data:image/") ? (
                <img src={reviewModal.soporte_url} alt="Soporte" className="max-h-80 mx-auto rounded border border-border" />
              ) : reviewModal.soporte_url ? (
                <a href={reviewModal.soporte_url} target="_blank" rel="noreferrer" className="block text-agro-green text-sm text-center bg-app p-4 rounded">Ver archivo →</a>
              ) : <div className="text-text-muted text-center py-6">Sin archivo</div>}
              {canManage && (
                <div className="flex justify-end gap-2 border-t border-border/60 pt-3">
                  <button onClick={async () => {
                    const motivo = prompt("Motivo del rechazo:"); if (!motivo) return;
                    try { await api.post(`/commissions/accruals/${reviewModal.id}/rechazar-soporte`, { motivo }); toast.success("Soporte rechazado. La parte retenida quedó anulada."); setReviewModal(null); load(); } catch (er) { toast.error(apiErr(er)); }
                  }} data-testid="review-reject" className="px-4 py-2 text-sm border border-red-500/40 text-red-400 rounded-md hover:bg-red-500/10">Rechazar</button>
                  <button onClick={async () => {
                    try { await api.post(`/commissions/accruals/${reviewModal.id}/liberar`); toast.success(`L. ${reviewModal.monto_retenido.toFixed(2)} liberados`); setReviewModal(null); load(); } catch (er) { toast.error(apiErr(er)); }
                  }} data-testid="review-release" className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md hover:bg-agro-green-hover">Aprobar y liberar</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

// ====================== TAB: TABLA GRATIVA ======================
function BonificationTable({ canManage, productCategories = [] }) {
  const [rows, setRows] = useState([]);
  const [cfg, setCfg] = useState({ seguimiento_retenido_percent: 25, requiere_soporte: true });
  const [edit, setEdit] = useState({});           // {id: {percent, modo, ...}}
  const [open, setOpen] = useState(false);
  const emptyRow = { categoria: "Semillas", nivel: "", percent: 0, modo: "fijo", descripcion: "", orden: 99, activa: true, es_campana: false, vigente_desde: "", vigente_hasta: "" };
  const [nuevo, setNuevo] = useState(emptyRow);

  const load = async () => {
    const [{ data }, { data: c }] = await Promise.all([
      api.get("/commissions/bonification-table"),
      api.get("/commissions/config"),
    ]);
    setRows(data); setCfg(c);
  };
  useEffect(() => { load(); }, []);

  const save = async (r) => {
    const patch = edit[r.id] || {};
    if (!Object.keys(patch).length) return;
    try {
      const merged = { ...r, ...patch };
      await api.put(`/commissions/bonification-table/${r.id}`, { ...merged, percent: Number(merged.percent), vigente_desde: merged.vigente_desde || null, vigente_hasta: merged.vigente_hasta || null });
      toast.success("Fila actualizada");
      setEdit({ ...edit, [r.id]: undefined });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (r) => {
    if (!window.confirm(`Eliminar ${r.categoria} ${r.nivel}?`)) return;
    try { await api.delete(`/commissions/bonification-table/${r.id}`); toast.success("Eliminada"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/commissions/bonification-table", { ...nuevo, percent: Number(nuevo.percent), orden: Number(nuevo.orden), vigente_desde: nuevo.vigente_desde || null, vigente_hasta: nuevo.vigente_hasta || null });
      toast.success("Fila agregada"); setOpen(false); setNuevo(emptyRow); load();
    }
    catch (e) { toast.error(apiErr(e)); }
  };
  const saveCfg = async () => {
    try { await api.put("/commissions/config", { ...cfg, seguimiento_retenido_percent: Number(cfg.seguimiento_retenido_percent) }); toast.success("Configuración guardada"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const getVal = (r, k) => (edit[r.id]?.[k] ?? r[k]);

  const nm = useModalForm({ value: nuevo, abierto: open, onClose: () => setOpen(false), onSave: create });
  return (
    <div className="space-y-4">
      {/* Config global */}
      <div className="bg-card border border-border/60 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <GearIcon size={16} className="text-agro-yellow" />
          <h4 className="font-heading text-sm uppercase tracking-wider text-text-secondary">Configuración global GRATIVA</h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">% retenido sin soporte</label>
            <input type="number" step="0.5" value={cfg.seguimiento_retenido_percent} onChange={(e) => setCfg({ ...cfg, seguimiento_retenido_percent: e.target.value })} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" data-testid="cfg-retenido" />
          </div>
          <div className="flex items-center gap-2 pb-2">
            <input id="rs" type="checkbox" checked={cfg.requiere_soporte} onChange={(e) => setCfg({ ...cfg, requiere_soporte: e.target.checked })} className="w-4 h-4 accent-agro-green" data-testid="cfg-requiere-soporte" />
            <label htmlFor="rs" className="text-sm text-text-primary">Requiere soporte (visita / cobro) para liberar</label>
          </div>
          {canManage && (
            <button onClick={saveCfg} className="bg-agro-green text-black font-medium rounded-md px-4 py-2 text-sm flex items-center justify-center gap-2" data-testid="save-cfg-btn"><FloppyDiskIcon size={16} weight="bold" /> Guardar configuración</button>
          )}
        </div>
        <p className="text-[11px] text-text-muted mt-3">Cuando un cobro RTC se autorice, el {cfg.seguimiento_retenido_percent}% de la GRATIVA quedará pendiente hasta que el RTC adjunte evidencia (visita, gestión de cobro, etc).</p>
      </div>

      {/* Tabla editable */}
      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <div className="p-3 border-b border-border/60 flex items-center justify-between">
          <div>
            <h4 className="font-heading text-sm uppercase tracking-wider text-agro-green">Tabla GRATIVA base (editable)</h4>
            <p className="text-[11px] text-text-muted mt-0.5">% por categoría × nivel de precio (nivel aplicado en la línea de la factura). Modo "Margen" (P0) = (precio − costo PEPS real de la factura) × el % indicado; 100 = margen completo. Un cambio aplica a los cobros siguientes; lo ya calculado no cambia. Las filas de campaña (con vigencia) tienen prioridad y admiten categoría libre (categoría, subcategoría, familia, marca o proveedor).</p>
          </div>
          {canManage && <button onClick={() => setOpen(true)} className="bg-agro-green text-black px-3 py-1.5 text-xs rounded-md font-medium flex items-center gap-1" data-testid="add-bonif-btn"><PlusIcon size={14} weight="bold" /> Nueva fila</button>}
        </div>
        <table className="w-full">
          <thead className="bg-sidebar">
            <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
              <th className="p-3 pl-5">Categoría</th><th className="p-3">Nivel</th>
              <th className="p-3 text-right">% GRATIVA</th><th className="p-3">Modo</th>
              <th className="p-3">Descripción</th><th className="p-3">Vigencia</th><th className="p-3 text-center">Activa</th>
              <th className="p-3 text-right pr-5">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan="8" className="p-8 text-center text-text-muted">Sin filas</td></tr>
              : rows.map((r) => {
                const dirty = !!edit[r.id] && Object.keys(edit[r.id]).length > 0;
                return (
                  <tr key={r.id} className="border-t border-border/40 hover:bg-white/[0.02]" data-testid={`bonif-row-${r.categoria}-${r.nivel || "any"}`}>
                    <td className="p-3 pl-5"><input value={getVal(r, "categoria")} disabled={!canManage} onChange={(e) => setEdit({...edit, [r.id]: {...(edit[r.id]||{}), categoria: e.target.value}})} className="bg-app border border-border rounded px-2 py-1 text-sm w-32" /></td>
                    <td className="p-3"><input value={getVal(r, "nivel")} disabled={!canManage} onChange={(e) => setEdit({...edit, [r.id]: {...(edit[r.id]||{}), nivel: e.target.value}})} placeholder="—" className="bg-app border border-border rounded px-2 py-1 text-sm w-16" /></td>
                    <td className="p-3 text-right">
                      <input type="number" step="0.01" min="0" max={getVal(r, "modo") === "margen" ? 100 : undefined} value={getVal(r, "percent")} disabled={!canManage}
                        onChange={(e) => setEdit({...edit, [r.id]: {...(edit[r.id]||{}), percent: e.target.value}})} data-testid={`bonif-pct-${r.categoria}-${r.nivel || "any"}`}
                        className="bg-app border border-border rounded px-2 py-1 text-sm w-20 text-right" />
                      {getVal(r, "modo") === "margen" && <div className="text-[10px] text-agro-yellow mt-0.5">% del margen</div>}
                    </td>
                    <td className="p-3"><select value={getVal(r, "modo")} disabled={!canManage} onChange={(e) => setEdit({...edit, [r.id]: {...(edit[r.id]||{}), modo: e.target.value}})} className="bg-app border border-border rounded px-2 py-1 text-xs"><option value="fijo">Fijo (%)</option><option value="margen">Sobre margen</option></select></td>
                    <td className="p-3"><input value={getVal(r, "descripcion") ?? ""} disabled={!canManage} onChange={(e) => setEdit({...edit, [r.id]: {...(edit[r.id]||{}), descripcion: e.target.value}})} className="bg-app border border-border rounded px-2 py-1 text-xs w-full" /></td>
                    <td className="p-3 text-xs whitespace-nowrap">
                      {r.es_campana || r.vigente_desde || r.vigente_hasta ? (
                        <div className="flex flex-col gap-1">
                          <input type="date" value={getVal(r, "vigente_desde") || ""} disabled={!canManage} onChange={(e) => setEdit({...edit, [r.id]: {...(edit[r.id]||{}), vigente_desde: e.target.value}})} className="bg-app border border-border rounded px-1 py-0.5 text-[11px]" />
                          <input type="date" value={getVal(r, "vigente_hasta") || ""} disabled={!canManage} onChange={(e) => setEdit({...edit, [r.id]: {...(edit[r.id]||{}), vigente_hasta: e.target.value}})} className="bg-app border border-border rounded px-1 py-0.5 text-[11px]" />
                        </div>
                      ) : <span className="text-text-muted">Permanente</span>}
                    </td>
                    <td className="p-3 text-center"><input type="checkbox" checked={getVal(r, "activa")} disabled={!canManage} onChange={(e) => setEdit({...edit, [r.id]: {...(edit[r.id]||{}), activa: e.target.checked}})} className="accent-agro-green" /></td>
                    <td className="p-3 pr-5 text-right flex justify-end gap-1">
                      {canManage && dirty && <button onClick={() => save(r)} title="Guardar cambios" className="p-1.5 text-agro-green hover:bg-agro-green/10 rounded" data-testid={`save-bonif-${r.id}`}><FloppyDiskIcon size={16} weight="bold" /></button>}
                      {canManage && <button aria-label="Eliminar" onClick={() => del(r)} className="p-1.5 text-text-muted hover:text-red-400"><TrashIcon size={16} /></button>}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...nm.overlayProps}>
          <div className="bg-card border border-border rounded-lg w-full max-w-lg" data-testid="bonif-new-modal">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold">Nueva fila de la Tabla GRATIVA</h3>
              <button aria-label="Cerrar" onClick={nm.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
            </div>
            <form onSubmit={nm.guardar} className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Categoría * <span className="text-text-muted normal-case">{nuevo.es_campana ? "(libre)" : "(catálogo)"}</span></label>
                  {nuevo.es_campana ? (
                    <>
                      <input required list="bonif-cats" value={nuevo.categoria} onChange={(e) => setNuevo({...nuevo, categoria: e.target.value})} placeholder="Categoría, línea, marca o proveedor" className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" data-testid="bonif-new-categoria" />
                      <datalist id="bonif-cats">{productCategories.map((c) => <option key={c} value={c} />)}</datalist>
                    </>
                  ) : (
                    <select required value={nuevo.categoria} onChange={(e) => setNuevo({...nuevo, categoria: e.target.value})} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" data-testid="bonif-new-categoria">
                      <option value="">— Selecciona —</option>
                      {productCategories.map((c) => <option key={c} value={c}>{c}</option>)}
                      <option value="Semillas">Semillas (flag es_semilla)</option>
                      <option value="Promociones">Promociones (código promocional)</option>
                      <option value="Producto">Producto (genérico / fallback)</option>
                    </select>
                  )}
                </div>
                <div><label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Nivel</label><input value={nuevo.nivel} onChange={(e) => setNuevo({...nuevo, nivel: e.target.value})} placeholder="P1, P2, P3, P4, P0 o vacío" className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" data-testid="bonif-new-nivel" /></div>
                <div><label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Modo *</label><select value={nuevo.modo} onChange={(e) => setNuevo({...nuevo, modo: e.target.value})} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm"><option value="fijo">Fijo (% sobre pago)</option><option value="margen">Sobre margen real</option></select></div>
                <div><label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">% GRATIVA</label><input type="number" step="0.01" value={nuevo.percent} onChange={(e) => setNuevo({...nuevo, percent: e.target.value})} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" data-testid="bonif-new-percent" /></div>
              </div>
              <div className="bg-app/40 border border-border/50 rounded-md p-3 space-y-2">
                <label className="flex items-center gap-2 text-xs font-semibold text-text-secondary">
                  <input type="checkbox" checked={nuevo.es_campana} onChange={(e) => setNuevo({...nuevo, es_campana: e.target.checked})} className="accent-agro-green" data-testid="bonif-new-campana" />
                  Fila de campaña (vigencia + categoría libre)
                </label>
                {nuevo.es_campana && (
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-[10px] uppercase text-text-muted">Vigente desde</label><input type="date" value={nuevo.vigente_desde} onChange={(e) => setNuevo({...nuevo, vigente_desde: e.target.value})} className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm" /></div>
                    <div><label className="text-[10px] uppercase text-text-muted">Vigente hasta</label><input type="date" value={nuevo.vigente_hasta} onChange={(e) => setNuevo({...nuevo, vigente_hasta: e.target.value})} className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm" /></div>
                  </div>
                )}
              </div>
              <div><label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Descripción</label><input value={nuevo.descripcion} onChange={(e) => setNuevo({...nuevo, descripcion: e.target.value})} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" /></div>
              <div className="flex justify-end gap-2 border-t border-border/60 pt-3">
                <button type="button" onClick={nm.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary">Cancelar</button>
                <button disabled={nm.saving} type="submit" className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md" data-testid="bonif-new-save">Agregar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ====================== TAB: TABLA DE PUNTUALIDAD ======================
function PunctualityTable({ canManage }) {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState({});
  const [open, setOpen] = useState(false);
  const [nuevo, setNuevo] = useState({ descripcion: "", dias_min: 0, dias_max: 0, factor: 1, orden: 99, activa: true });

  const load = async () => { const { data } = await api.get("/commissions/punctuality-table"); setRows(data); };
  useEffect(() => { load(); }, []);

  const getVal = (r, k) => (edit[r.id]?.[k] ?? r[k]);
  const save = async (r) => {
    const patch = edit[r.id] || {};
    if (!Object.keys(patch).length) return;
    try {
      await api.put(`/commissions/punctuality-table/${r.id}`, { ...r, ...patch,
        dias_min: Number(patch.dias_min ?? r.dias_min),
        dias_max: Number(patch.dias_max ?? r.dias_max),
        factor: Number(patch.factor ?? r.factor),
      });
      toast.success("Bracket actualizado");
      setEdit({ ...edit, [r.id]: undefined }); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (r) => { if (!window.confirm(`Eliminar ${r.descripcion}?`)) return; try { await api.delete(`/commissions/punctuality-table/${r.id}`); toast.success("Eliminado"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/commissions/punctuality-table", { ...nuevo, dias_min: Number(nuevo.dias_min), dias_max: Number(nuevo.dias_max), factor: Number(nuevo.factor), orden: Number(nuevo.orden) }); toast.success("Bracket agregado"); setOpen(false); setNuevo({ descripcion: "", dias_min: 0, dias_max: 0, factor: 1, orden: 99, activa: true }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const nm = useModalForm({ value: nuevo, abierto: open, onClose: () => setOpen(false), onSave: create });
  return (
    <div className="space-y-4">
      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <div className="p-3 border-b border-border/60 flex items-center justify-between">
          <div>
            <h4 className="font-heading text-sm uppercase tracking-wider text-agro-green">Factor por puntualidad de pago (editable)</h4>
            <p className="text-[11px] text-text-muted mt-0.5">Días de atraso = fecha de pago − fecha de vencimiento. Valores negativos = contado (pago antes del vencimiento).</p>
          </div>
          {canManage && <button onClick={() => setOpen(true)} className="bg-agro-green text-black px-3 py-1.5 text-xs rounded-md font-medium flex items-center gap-1" data-testid="add-punct-btn"><PlusIcon size={14} weight="bold" /> Nuevo bracket</button>}
        </div>
        <table className="w-full">
          <thead className="bg-sidebar">
            <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
              <th className="p-3 pl-5">Condición</th>
              <th className="p-3 text-right">Días desde</th><th className="p-3 text-right">Días hasta</th>
              <th className="p-3 text-right">Factor</th><th className="p-3 text-center">Activo</th>
              <th className="p-3 text-right pr-5">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan="6" className="p-8 text-center text-text-muted">Sin filas</td></tr>
              : rows.map((r) => {
                const dirty = !!edit[r.id] && Object.keys(edit[r.id]).length > 0;
                return (
                  <tr key={r.id} className="border-t border-border/40 hover:bg-white/[0.02]" data-testid={`punct-row-${r.id}`}>
                    <td className="p-3 pl-5"><input value={getVal(r, "descripcion")} disabled={!canManage} onChange={(e) => setEdit({...edit, [r.id]: {...(edit[r.id]||{}), descripcion: e.target.value}})} className="bg-app border border-border rounded px-2 py-1 text-sm w-full" /></td>
                    <td className="p-3 text-right"><input type="number" value={getVal(r, "dias_min")} disabled={!canManage} onChange={(e) => setEdit({...edit, [r.id]: {...(edit[r.id]||{}), dias_min: e.target.value}})} className="bg-app border border-border rounded px-2 py-1 text-sm w-24 text-right" /></td>
                    <td className="p-3 text-right"><input type="number" value={getVal(r, "dias_max")} disabled={!canManage} onChange={(e) => setEdit({...edit, [r.id]: {...(edit[r.id]||{}), dias_max: e.target.value}})} className="bg-app border border-border rounded px-2 py-1 text-sm w-24 text-right" /></td>
                    <td className="p-3 text-right"><input type="number" step="0.01" value={getVal(r, "factor")} disabled={!canManage} onChange={(e) => setEdit({...edit, [r.id]: {...(edit[r.id]||{}), factor: e.target.value}})} className="bg-app border border-border rounded px-2 py-1 text-sm w-20 text-right" /> <span className="text-xs text-text-muted">({(getVal(r,"factor")*100).toFixed(0)}%)</span></td>
                    <td className="p-3 text-center"><input type="checkbox" checked={getVal(r, "activa")} disabled={!canManage} onChange={(e) => setEdit({...edit, [r.id]: {...(edit[r.id]||{}), activa: e.target.checked}})} className="accent-agro-green" /></td>
                    <td className="p-3 pr-5 text-right flex justify-end gap-1">
                      {canManage && dirty && <button onClick={() => save(r)} title="Guardar" className="p-1.5 text-agro-green hover:bg-agro-green/10 rounded" data-testid={`save-punct-${r.id}`}><FloppyDiskIcon size={16} weight="bold" /></button>}
                      {canManage && <button aria-label="Eliminar" onClick={() => del(r)} className="p-1.5 text-text-muted hover:text-red-400"><TrashIcon size={16} /></button>}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...nm.overlayProps}>
          <div className="bg-card border border-border rounded-lg w-full max-w-md" data-testid="punct-new-modal">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold">Nuevo bracket de puntualidad</h3>
              <button aria-label="Cerrar" onClick={nm.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
            </div>
            <form onSubmit={nm.guardar} className="p-5 space-y-3">
              <div><label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Descripción *</label><input required value={nuevo.descripcion} onChange={(e) => setNuevo({...nuevo, descripcion: e.target.value})} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" data-testid="punct-new-desc" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Días desde</label><input type="number" value={nuevo.dias_min} onChange={(e) => setNuevo({...nuevo, dias_min: e.target.value})} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" /></div>
                <div><label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Días hasta</label><input type="number" value={nuevo.dias_max} onChange={(e) => setNuevo({...nuevo, dias_max: e.target.value})} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" /></div>
              </div>
              <div><label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Factor (0.00 a 1.00)</label><input type="number" step="0.01" value={nuevo.factor} onChange={(e) => setNuevo({...nuevo, factor: e.target.value})} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" /></div>
              <div className="flex justify-end gap-2 border-t border-border/60 pt-3">
                <button type="button" onClick={nm.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary">Cancelar</button>
                <button disabled={nm.saving} type="submit" className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md" data-testid="punct-new-save">Agregar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ====================== TAB: REPORTE (7.6) ======================
function CommissionReport({ reps }) {
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  const ymdL = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const [f, setF] = useState({ desde: ymdL(first), hasta: ymdL(today), rtc_id: "" });
  const [data, setData] = useState(null);
  const [vista, setVista] = useState("por_rtc");
  const [loading, setLoading] = useState(false);

  const params = () => Object.fromEntries(Object.entries(f).filter(([, v]) => v));
  const load = async () => {
    setLoading(true);
    try { const { data: d } = await api.get("/commissions/report", { params: params() }); setData(d); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const exportar = async (formato) => {
    try {
      const res = await api.get("/commissions/report/export", { params: { ...params(), formato }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `grativa_rtc_${f.desde}_${f.hasta}.${formato}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const vistas = {
    por_rtc: { l: "Por RTC", cols: [["rtc_nombre", "RTC"]] },
    por_categoria: { l: "Por línea", cols: [["categoria", "Línea / categoría"]] },
    por_proveedor: { l: "Por proveedor", cols: [["proveedor_nombre", "Proveedor"]] },
    por_rtc_linea: { l: "RTC × línea × proveedor", cols: [["rtc_nombre", "RTC"], ["categoria", "Línea"], ["proveedor_nombre", "Proveedor"]] },
    por_periodo: { l: "Por período", cols: [["periodo", "Período"], ["rtc_nombre", "RTC"]] },
  };
  const rows = (data && data[vista]) || [];
  const v = vistas[vista];

  return (
    <div className="space-y-4" data-testid="commission-report">
      <div className="bg-card border border-border/60 rounded-lg p-4 flex flex-wrap gap-3 items-end">
        <div><label className="text-[10px] uppercase text-text-muted block mb-1">Desde</label><input type="date" value={f.desde} onChange={(e) => setF({ ...f, desde: e.target.value })} className="bg-app border border-border rounded-md px-3 py-1.5 text-sm" /></div>
        <div><label className="text-[10px] uppercase text-text-muted block mb-1">Hasta</label><input type="date" value={f.hasta} onChange={(e) => setF({ ...f, hasta: e.target.value })} className="bg-app border border-border rounded-md px-3 py-1.5 text-sm" /></div>
        <div><label className="text-[10px] uppercase text-text-muted block mb-1">RTC</label>
          <select value={f.rtc_id} onChange={(e) => setF({ ...f, rtc_id: e.target.value })} className="bg-app border border-border rounded-md px-3 py-1.5 text-sm">
            <option value="">Todos</option>
            {reps.map((r) => <option key={r.id} value={r.id}>{r.codigo} · {r.nombre}</option>)}
          </select>
        </div>
        <button onClick={load} className="bg-agro-green text-black font-medium rounded-md px-4 py-2 text-sm" data-testid="report-run">{loading ? "Cargando…" : "Consultar"}</button>
        <div className="ml-auto flex gap-2">
          <button onClick={() => exportar("xlsx")} className="border border-agro-green/40 text-agro-green rounded-md px-3 py-2 text-xs flex items-center gap-1" data-testid="report-xlsx"><FileXlsIcon size={14} /> Excel</button>
          <button onClick={() => exportar("pdf")} className="border border-red-400/40 text-red-300 rounded-md px-3 py-2 text-xs flex items-center gap-1" data-testid="report-pdf"><FilePdfIcon size={14} /> PDF</button>
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[["GRATIVA bruta", data.total_bruta, "text-text-primary"], ["Liberada", data.total_liberada, "text-agro-green"], ["Retenida", data.total_retenida, "text-orange-300"], ["Anulada", data.total_anulada, "text-red-400"], ["Pagada", data.total_pagada, "text-agro-yellow"]].map(([l, val, c]) => (
            <div key={l} className="bg-card border border-border/60 rounded-lg p-3">
              <div className="text-[10px] uppercase text-text-muted">{l}</div>
              <div className={`font-heading text-lg font-semibold ${c}`}>{fmtL(val || 0)}</div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <div className="p-2 border-b border-border/60 flex gap-1 flex-wrap">
          {Object.entries(vistas).map(([k, x]) => (
            <button key={k} onClick={() => setVista(k)} className={`px-3 py-1 text-xs rounded ${vista === k ? "bg-agro-green/15 text-agro-green" : "text-text-secondary hover:text-text-primary"}`}>{x.l}</button>
          ))}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
              <tr className="text-left">
                {v.cols.map(([, l]) => <th key={l} className="p-3">{l}</th>)}
                <th className="p-3 text-right">Líneas</th><th className="p-3 text-right">Cobrado</th><th className="p-3 text-right">GRATIVA bruta</th>
                <th className="p-3 text-right">Liberada</th><th className="p-3 text-right">Retenida</th><th className="p-3 text-right">Pagada</th><th className="p-3 text-right pr-5">Por pagar</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={v.cols.length + 7} className="p-8 text-center text-text-muted">Sin GRATIVA en el período</td></tr>
                : rows.map((r, i) => (
                  <tr key={i} className="border-t border-border/40">
                    {v.cols.map(([k]) => <td key={k} className="p-3 text-xs">{r[k] || "—"}</td>)}
                    <td className="p-3 text-right text-xs">{r.lineas}</td>
                    <td className="p-3 text-right text-xs">{fmtL(r.cobrado)}</td>
                    <td className="p-3 text-right text-xs">{fmtL(r.bruta)}</td>
                    <td className="p-3 text-right text-xs text-agro-green">{fmtL(r.liberada)}</td>
                    <td className="p-3 text-right text-xs text-orange-300">{fmtL(r.retenida)}</td>
                    <td className="p-3 text-right text-xs text-agro-yellow">{fmtL(r.pagada)}</td>
                    <td className="p-3 pr-5 text-right text-xs font-medium">{fmtL(r.por_pagar)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
      {data && <div className="text-[11px] text-text-muted">Período por fecha de pago · {fmtDate(f.desde)} – {fmtDate(f.hasta)}</div>}
    </div>
  );
}
